import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Box, Typography, Avatar, Chip, Divider,
  CircularProgress, Alert, IconButton, Paper, Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import { events as eventsApi } from "../api/endpoints";
import type { Event, EventParticipant } from "../types";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { NAVY, CRIMSON, CARD_TINT_GRADIENT } from "../theme/theme";
import { alpha } from "@mui/material/styles";

const STATUS_COLORS: Record<string, string> = {
  scheduled: "#2563EB",
  ongoing: "#059669",
  completed: "#6B7280",
  cancelled: "#DC2626",
};

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  const [event, setEvent] = useState<Event | null>(null);
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [userRsvp, setUserRsvp] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const evId = Number(id);
    Promise.all([
      eventsApi.get(evId),
      eventsApi.participants(evId),
    ]).then(([ev, parts]) => {
      setEvent(ev ?? null);
      setParticipants(parts);
    }).catch((err) => {
      setError(err instanceof Error ? err.message : t("events.loadError"));
    }).finally(() => setLoading(false));
  }, [id]);

  const handleRsvp = async (status: "going" | "interested" | "maybe") => {
    if (!event) return;
    setRsvpLoading(true);
    try {
      await eventsApi.rsvp(event.id, status);
      setUserRsvp(status);
      const updated = await eventsApi.participants(event.id);
      setParticipants(updated);
    } catch { /* ignore */ } finally {
      setRsvpLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Alert severity="error">{error ?? t("events.notFound")}</Alert>
      </Container>
    );
  }

  const startDate = new Date(event.start_time);
  const endDate = event.end_time ? new Date(event.end_time) : null;

  return (
    <>
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5, py: 1.5,
        overflow: "hidden",
        color: "white",
        background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a7a 55%, ${alpha(CRIMSON, 0.92)} 100%)`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: -40, right: -20,
          width: 130, height: 130,
          borderRadius: "50%",
          background: alpha("#E8A020", 0.18),
          filter: "blur(30px)",
          pointerEvents: "none",
        },
      }}
    >
      <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: "white", position: "relative", zIndex: 1 }}>
        <ArrowBackIcon />
      </IconButton>
      <Typography variant="subtitle1" fontWeight={700} sx={{ position: "relative", zIndex: 1 }}>
        {t("common.events")}
      </Typography>
    </Box>
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ border: `1px solid ${alpha(NAVY, 0.1)}`, borderRadius: 3, p: 3, mb: 2, backgroundImage: CARD_TINT_GRADIENT, backgroundColor: "#FFFFFF" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              background: "linear-gradient(135deg, #DC2626 0%, #B91C2C 100%)",
              width: 56,
              height: 56,
              borderRadius: 3,
            }}
          >
            <EventIcon sx={{ fontSize: 26 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2} mb={0.5}>
              {event.title}
            </Typography>
            <Chip
              label={t(`events.${event.event_status}`) || event.event_status}
              size="small"
              sx={{
                fontSize: "0.65rem",
                bgcolor: STATUS_COLORS[event.event_status] ?? "#6B7280",
                color: "white",
                fontWeight: 700,
              }}
            />
          </Box>
        </Box>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2, whiteSpace: "pre-wrap" }}>
          {event.description}
        </Typography>

        {/* RSVP buttons — signed-in users only */}
        {user && (event.event_status === "scheduled" || event.event_status === "ongoing") ? (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {(["going", "interested", "maybe"] as const).map((status) => (
              <Button
                key={status}
                size="small"
                variant={userRsvp === status ? "contained" : "outlined"}
                disabled={rsvpLoading}
                onClick={() => handleRsvp(status)}
                sx={{
                  borderRadius: 10,
                  textTransform: "capitalize",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  ...(userRsvp === status && { color: "white" }),
                }}
              >
                {t(`events.${status}`) || status}
              </Button>
            ))}
          </Box>
        ) : null}
      </Paper>

      {/* Details card */}
      <Paper elevation={0} sx={{ border: `1px solid ${alpha(NAVY, 0.1)}`, borderRadius: 3, p: 2.5, mb: 2, backgroundImage: CARD_TINT_GRADIENT, backgroundColor: "#FFFFFF" }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>{t("events.detailsSection")}</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.3 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.1 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>{t("events.start")}</Typography>
              <Typography variant="body2" color="text.secondary">{startDate.toLocaleString()}</Typography>
              {endDate && (
                <>
                  <Typography variant="body2" fontWeight={600} mt={0.5}>{t("events.end")}</Typography>
                  <Typography variant="body2" color="text.secondary">{endDate.toLocaleString()}</Typography>
                </>
              )}
            </Box>
          </Box>

          {event.location && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <LocationOnIcon sx={{ fontSize: 18, color: "text.secondary", mt: 0.1 }} />
              <Typography variant="body2">
                {[
                  event.location.address_line1,
                  event.location.city,
                  event.location.state_province,
                  event.location.country_code,
                ].filter(Boolean).join(", ")}
              </Typography>
            </Box>
          )}

          {event.creator && (
            <>
              <Divider />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
                onClick={() => navigate(`/app/profile/${event.creator!.username}`)}
              >
                <PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight={600} sx={{ "&:hover": { color: CRIMSON } }}>
                  {t("events.organizer")}: {event.creator.first_name} {event.creator.last_name}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* Participants */}
      {participants.length > 0 && (
        <Paper elevation={0} sx={{ border: `1px solid ${alpha(NAVY, 0.1)}`, borderRadius: 3, p: 2.5, mb: 2, backgroundImage: CARD_TINT_GRADIENT, backgroundColor: "#FFFFFF" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <GroupIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="subtitle2" fontWeight={700}>
              {t("events.participants")} ({participants.length})
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {participants.map((p) => (
              <Box
                key={p.user_id}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: p.user ? "pointer" : "default" }}
                onClick={() => { if (p.user) navigate(`/app/profile/${p.user.username}`); }}
              >
                <Avatar sx={{ width: 32, height: 32, background: "linear-gradient(135deg, #1a3a7a 0%, #0F2156 100%)", fontSize: "0.78rem", fontWeight: 700 }}>
                  {p.user?.first_name?.charAt(0) ?? "?"}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ "&:hover": p.user ? { color: CRIMSON } : {} }}>
                    {p.user ? `${p.user.first_name} ${p.user.last_name}` : `User ${p.user_id}`}
                  </Typography>
                </Box>
                <Chip
                  label={t(`events.${p.rsvp_status}`) || p.rsvp_status}
                  size="small"
                  sx={{ fontSize: "0.62rem", fontWeight: 600 }}
                />
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Container>
    </>
  );
}
